import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  // 🎯 Keyframes para animações suaves
  "@keyframes shimmer": {
    "0%": {
      backgroundPosition: "-200px 0",
    },
    "100%": {
      backgroundPosition: "200px 0",
    },
  },
  
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
    "@keyframes pulse": {
    "0%, 100%": {
      opacity: 0.8,
    },
    "50%": {
      opacity: 1,
    },
  },
  
  "@keyframes slideInFromBottom": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px) scale(0.95)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },
  
  "@keyframes breathe": {
    "0%, 100%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.02)",
    },
  },
  
  "@keyframes progressBar": {
    "0%": {
      width: "0%",
    },
    "100%": {
      width: "100%",
    },
  },
    // 🎯 Container principal
  skeletonContainer: {
    padding: "20px",
    animation: "$fadeIn 0.5s ease",
    position: "relative",
    
    // Barra de progresso sutil no topo
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      height: "2px",
      backgroundColor: theme.palette.primary.main,
      animation: "$progressBar 3s ease-in-out infinite",
      opacity: 0.6,
      borderRadius: "0 0 2px 2px",
    },
  },
    // 🎯 Container de mensagem skeleton
  messageContainer: {
    display: "flex",
    marginBottom: "12px",
    animation: "$slideInFromBottom 0.6s ease",
    
    "&.fromMe": {
      justifyContent: "flex-end",
    },
    "&.fromContact": {
      justifyContent: "flex-start",
    },
    
    // Espaçamento dinâmico baseado no tipo de mensagem
    "&.single": {
      marginBottom: "8px",
    },
    "&.grouped": {
      marginBottom: "4px",
    },
    "&.lastInGroup": {
      marginBottom: "16px",
    },
    
    // Hover effect sutil para interatividade
    "&:hover": {
      "& $messageBubble": {
        transform: "scale(1.01)",
        transition: "transform 0.2s ease",
      },
    },
  },
    // 🎯 Bolha de mensagem skeleton
  messageBubble: {
    borderRadius: "8px",
    padding: "10px 14px",
    minWidth: "80px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    
    "&.fromMe": {
      backgroundColor: theme.mode === "light" ? "#dcf8c6" : "#005c4b",
      borderBottomRightRadius: "4px",
    },
    "&.fromContact": {
      backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
      borderBottomLeftRadius: "4px",
    },
    
    // Variações de tamanho
    "&.tiny": {
      maxWidth: "120px",
      minWidth: "60px",
      animation: "$breathe 4s ease-in-out infinite",
    },
    "&.small": {
      maxWidth: "200px",
      minWidth: "100px",
      animation: "$breathe 5s ease-in-out infinite",
    },
    "&.medium": {
      maxWidth: "300px",
      minWidth: "150px",
      animation: "$breathe 6s ease-in-out infinite",
    },
    "&.large": {
      maxWidth: "400px",
      minWidth: "200px",
      animation: "$breathe 7s ease-in-out infinite",
    },
    "&.extraLarge": {
      maxWidth: "500px",
      minWidth: "250px",
      animation: "$breathe 8s ease-in-out infinite",
    },
    
    // Efeito shimmer mais realista
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-200px",
      width: "200px",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)",
      animation: "$shimmer 2.5s infinite linear",
      zIndex: 1,
    },
    
    "&.typing::before": {
      animation: "$pulse 1.5s infinite ease-in-out",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
    },
  },
  
  // 🎯 Linhas de texto skeleton
  skeletonLine: {
    borderRadius: "4px",
    marginBottom: "6px",
    position: "relative",
    zIndex: 2,
    backgroundColor: theme.mode === "light" 
      ? "rgba(0, 0, 0, 0.08)" 
      : "rgba(255, 255, 255, 0.08)",
    
    "&:last-child": {
      marginBottom: 0,
    },
    
    // Diferentes alturas para linhas
    "&.textLine": {
      height: "14px",
    },
    "&.boldLine": {
      height: "16px",
      backgroundColor: theme.mode === "light" 
        ? "rgba(0, 0, 0, 0.12)" 
        : "rgba(255, 255, 255, 0.12)",
    },
    "&.timestamp": {
      height: "10px",
      marginTop: "6px",
      width: "45px !important",
      opacity: 0.6,
    },
    
    // Larguras variadas para parecer texto real
    "&.w20": { width: "20%" },
    "&.w30": { width: "30%" },
    "&.w40": { width: "40%" },
    "&.w50": { width: "50%" },
    "&.w60": { width: "60%" },
    "&.w70": { width: "70%" },
    "&.w80": { width: "80%" },
    "&.w90": { width: "90%" },
    "&.w100": { width: "100%" },
  },
  
  // 🎯 Skeleton para mídia
  mediaSkeleton: {
    borderRadius: "8px",
    marginBottom: "8px",
    backgroundColor: theme.mode === "light" 
      ? "rgba(0, 0, 0, 0.05)" 
      : "rgba(255, 255, 255, 0.05)",
    position: "relative",
    overflow: "hidden",
    
    "&.image": {
      width: "200px",
      height: "150px",
    },
    "&.video": {
      width: "250px",
      height: "180px",
    },
    "&.audio": {
      width: "180px",
      height: "50px",
      borderRadius: "25px",
    },
    "&.document": {
      width: "220px",
      height: "60px",
      borderRadius: "8px",
    },
    
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-200px",
      width: "200px",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
      animation: "$shimmer 3s infinite linear",
    },
    
    // Ícone de mídia simulado
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      backgroundColor: theme.mode === "light" 
        ? "rgba(0, 0, 0, 0.1)" 
        : "rgba(255, 255, 255, 0.1)",
      zIndex: 2,
    },
  },
  
  // 🎯 Container para load more (mais compacto)
  loadMoreContainer: {
    padding: "10px 20px",
    animation: "$fadeIn 0.3s ease",
  },
    // 🎯 Indicador de digitação
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    borderRadius: "18px",
    maxWidth: "80px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    
    "& .dot": {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: theme.mode === "light" 
        ? "rgba(0, 0, 0, 0.3)" 
        : "rgba(255, 255, 255, 0.3)",
      animation: "$pulse 1.4s infinite ease-in-out",
      
      "&:nth-child(1)": { animationDelay: "0s" },
      "&:nth-child(2)": { animationDelay: "0.2s" },
      "&:nth-child(3)": { animationDelay: "0.4s" },
    },  },
  
  // 🎯 Smooth scroll hint
  scrollHint: {
    position: "absolute",
    bottom: "10px",
    right: "20px",
    fontSize: "10px",
    color: theme.palette.text.disabled,
    opacity: 0.6,
    animation: "$fadeIn 2s ease",
  },
}));

const SkeletonLoader = ({ 
  type = "initial", 
  count = 5, 
  showMedia = false,
  showTyping = false,
  className = "" 
}) => {
  const classes = useStyles();
  
  // 🎯 Gerar padrões de conversa aleatórios mas realistas
  const conversationPatterns = useMemo(() => {
    const patterns = [];
    const mediaTypes = ["image", "video", "audio", "document"];
    const bubbleSizes = ["tiny", "small", "medium", "large", "extraLarge"];
    const textWidths = ["w20", "w30", "w40", "w50", "w60", "w70", "w80", "w90", "w100"];
    
    // Simular uma conversa mais realista
    for (let i = 0; i < count; i++) {
      const isFromMe = Math.random() > 0.4; // 40% de chance de ser do usuário
      const hasMedia = showMedia && Math.random() > 0.7; // 30% chance de mídia
      const isGrouped = i > 0 && patterns[i-1]?.fromMe === isFromMe && Math.random() > 0.6;
      
      // Tipos de mensagem baseados em probabilidades reais
      let messageType;
      const rand = Math.random();
      if (rand > 0.85) messageType = "extraLarge"; // 15% mensagens longas
      else if (rand > 0.6) messageType = "medium"; // 25% mensagens médias
      else if (rand > 0.3) messageType = "small"; // 30% mensagens pequenas
      else messageType = "tiny"; // 30% mensagens muito pequenas
      
      // Número de linhas baseado no tamanho
      let lineCount;
      switch (messageType) {
        case "tiny": lineCount = 1; break;
        case "small": lineCount = Math.random() > 0.5 ? 1 : 2; break;
        case "medium": lineCount = Math.floor(Math.random() * 3) + 1; break;
        case "large": lineCount = Math.floor(Math.random() * 4) + 2; break;
        case "extraLarge": lineCount = Math.floor(Math.random() * 6) + 3; break;
        default: lineCount = 1;
      }
      
      // Gerar larguras de linha realistas
      const lineWidths = [];
      for (let j = 0; j < lineCount; j++) {
        if (j === lineCount - 1 && lineCount > 1) {
          // Última linha geralmente mais curta
          lineWidths.push(textWidths[Math.floor(Math.random() * 5)]); // 20-60%
        } else {
          // Outras linhas mais variadas
          lineWidths.push(textWidths[Math.floor(Math.random() * textWidths.length)]);
        }
      }
      
      patterns.push({
        fromMe: isFromMe,
        size: bubbleSizes[bubbleSizes.indexOf(messageType)],
        lines: lineCount,
        lineWidths,
        hasMedia,
        mediaType: hasMedia ? mediaTypes[Math.floor(Math.random() * mediaTypes.length)] : null,
        isGrouped,
        spacing: isGrouped ? "grouped" : "single",
        delay: i * 0.1, // Delay escalonado para animação
      });
    }
    
    // Marcar última mensagem de cada grupo
    for (let i = 0; i < patterns.length - 1; i++) {
      if (patterns[i].isGrouped && patterns[i+1].fromMe !== patterns[i].fromMe) {
        patterns[i].spacing = "lastInGroup";
      }
    }
    
    return patterns;
  }, [count, showMedia]);
  
  const renderMessageSkeleton = (pattern, index) => {
    const isFromMe = pattern.fromMe;
    const containerClass = isFromMe ? "fromMe" : "fromContact";
    const bubbleClass = `${isFromMe ? "fromMe" : "fromContact"} ${pattern.size}`;
    
    return (
      <div 
        key={index} 
        className={`${classes.messageContainer} ${containerClass} ${pattern.spacing}`}
        style={{
          animationDelay: `${pattern.delay}s`,
        }}
      >
        <div className={`${classes.messageBubble} ${bubbleClass}`}>
          {/* Skeleton para mídia */}
          {pattern.hasMedia && (
            <div className={`${classes.mediaSkeleton} ${pattern.mediaType}`} />
          )}
          
          {/* Linhas de texto */}
          {Array.from({ length: pattern.lines }, (_, lineIndex) => {
            const isLastLine = lineIndex === pattern.lines - 1;
            const lineType = Math.random() > 0.9 ? "boldLine" : "textLine"; // 10% linhas em negrito
            
            return (
              <div 
                key={lineIndex}
                className={`${classes.skeletonLine} ${lineType} ${pattern.lineWidths[lineIndex] || "w80"}`}
              />
            );
          })}
          
          {/* Timestamp skeleton */}
          <div className={`${classes.skeletonLine} timestamp`} />
        </div>
      </div>
    );
  };
  
  const renderLoadMoreSkeleton = (index) => {
    const isFromMe = Math.random() > 0.5;
    const containerClass = isFromMe ? "fromMe" : "fromContact";
    const bubbleClass = `${isFromMe ? "fromMe" : "fromContact"} small`;
    
    return (
      <div 
        key={index} 
        className={`${classes.messageContainer} ${containerClass} single`}
      >
        <div className={`${classes.messageBubble} ${bubbleClass}`}>
          <div className={`${classes.skeletonLine} textLine w70`} />
          <div className={`${classes.skeletonLine} timestamp`} />
        </div>
      </div>
    );
  };
    const renderTypingIndicator = () => (
    <div className={`${classes.messageContainer} fromContact single`}>
      <div className={classes.typingIndicator}>
        <div className="dot" />
        <div className="dot" />
        <div className="dot" />
      </div>
    </div>
  );
  if (type === "loadMore") {
    return (
      <div className={`${classes.loadMoreContainer} ${className}`}>
        {Array.from({ length: Math.min(count, 3) }, (_, index) => 
          renderLoadMoreSkeleton(index)
        )}
      </div>
    );
  }
    // Skeleton inicial (mais completo e realista)
  return (
    <div className={`${classes.skeletonContainer} ${className}`}>
      {conversationPatterns.map((pattern, index) => 
        renderMessageSkeleton(pattern, index)
      )}
      {showTyping && renderTypingIndicator()}
      
      {/* Scroll hint sutil */}
      {type === "initial" && (
        <div className={classes.scrollHint}>
          Deslize para carregar mais
        </div>
      )}
    </div>
  );
};

SkeletonLoader.propTypes = {
  /** Tipo do skeleton: 'initial' para carregamento inicial, 'loadMore' para carregar mais */
  type: PropTypes.oneOf(['initial', 'loadMore']),
  /** Quantidade de skeletons a renderizar */
  count: PropTypes.number,
  /** Se deve mostrar skeletons de mídia ocasionalmente */
  showMedia: PropTypes.bool,
  /** Se deve mostrar indicador de digitação */
  showTyping: PropTypes.bool,
  /** Classes CSS adicionais */
  className: PropTypes.string,
};

SkeletonLoader.defaultProps = {
  type: "initial",
  count: 5,
  showMedia: false,
  showTyping: false,
  className: "",
};

export default SkeletonLoader;
